import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import toast from 'react-hot-toast';
import { Spade, Heart, Diamond, Club } from 'lucide-react';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signInWithUsername, user } = useAuth();

  // Redireccionar automàticament si ja està autenticat
  useEffect(() => {
    if (user) {
      navigate('/inici', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Special handling for admin - exact username "admin47465"
    if (username === 'admin47465') {
      // Admin login - proceed directly
    } else {
      // Regular user - validate format: username + 4 digits, all lowercase
      const usernameRegex = /^[a-z]+\d{4}$/;
      if (!usernameRegex.test(username)) {
        toast.error("El format ha de ser: nom d'usuari + 4 números (exemple: cesc1234)");
        setLoading(false);
        return;
      }

      // Extract display username (without last 4 digits)
      const displayUsername = username.slice(0, -4);

      // Prevent any usernames starting with "admin" (except admin47465)
      if (displayUsername.startsWith('admin')) {
        toast.error("No pots utilitzar 'admin' al teu nom d'usuari.");
        setLoading(false);
        return;
      }

      // Check for conflicts - simplified version
      try {
        const { createClient } = await import('../lib/supabase');
        const supabase = createClient();

        // Check if the exact username already exists
        const { data: existingUser, error: fetchError } = await supabase
          .from('profiles')
          .select('username')
          .eq('username', username)
          .maybeSingle();

        // If there's an error other than "not found", show it but continue anyway
        if (fetchError && fetchError.code !== 'PGRST116') {
          console.error('Error checking user:', fetchError);
        }

        // If user doesn't exist, check for base username conflicts
        if (!existingUser) {
          const { data: existingUsers } = await supabase
            .from('profiles')
            .select('username')
            .ilike('username', `${displayUsername}%`);

          if (existingUsers && existingUsers.length > 0) {
            const hasSameBase = existingUsers.some(
              (user: { username: string }) =>
                user.username.startsWith(displayUsername) &&
                user.username.length === displayUsername.length + 4
            );

            if (hasSameBase) {
              toast.error(
                `Ja existeix un usuari amb el nom "${displayUsername}". Prova amb un altre nom.`
              );
              setLoading(false);
              return;
            }
          }
        }
      } catch (error) {
        // Log error but continue with sign in attempt
        console.error('Conflict check error:', error);
      }
    }

    const { error } = await signInWithUsername(username);

    if (error) {
      toast.error(error);
      setLoading(false);
    } else {
      const displayUsername =
        username === 'admin47465' ? 'admin47465' : username.slice(0, -4);
      toast.success(`Benvingut, ${displayUsername}!`);
      navigate('/inici');
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Gradient background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-emerald-900/20 via-transparent to-transparent blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-red-900/20 via-transparent to-transparent blur-3xl"></div>
      </div>

      <div className="w-full max-w-5xl space-y-12 relative z-10 mt-12 sm:mt-16">
        {/* Hero Section */}
        <div className="text-center space-y-8">
          <div className="space-y-3">
            <h1 className="text-6xl sm:text-7xl md:text-8xl font-black text-white tracking-tight">
              Cartes 🃏
            </h1>
            <p className="text-xl sm:text-2xl text-zinc-400 font-semibold">
              Texas Hold'em Poker
            </p>
          </div>
        </div>

        {/* Login/Actions Section */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Login Card */}
          <Card className="border-zinc-800 bg-zinc-900/80 backdrop-blur-xl shadow-2xl">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-white">Accedeix a la plataforma</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-3">
                  <Label htmlFor="username" className="text-zinc-300 text-sm font-medium">
                    Format del nom d'usuari:
                  </Label>
                  <div className="space-y-2 text-xs text-zinc-400 mb-3">
                    <div className="flex items-start gap-2">
                      <span>•</span>
                      <p>Usuari en minúscules + 4 números</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span>•</span>
                      <p className="text-amber-400">Els 4 números actuen com a contrasenya. No els comparteixis amb ningú.</p>
                    </div>
                  </div>
                  <Input
                    id="username"
                    type="text"
                    placeholder="exemple: cesc1234"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    disabled={loading}
                    className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 h-12 text-base focus:border-emerald-500 focus:ring-emerald-500"
                    autoComplete="off"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !username}
                  className="w-full bg-white hover:bg-zinc-100 text-black font-semibold h-12 text-base shadow-lg transition-all"
                >
                  {loading ? 'Carregant...' : 'Entrar'}
                </Button>
              </form>
              
              <p className="text-xs text-zinc-500 mt-4 text-center">
                Si és la primera vegada, es crearà un compte nou amb 1000 fitxes de saldo inicial.
              </p>
            </CardContent>
          </Card>

          {/* Practice Mode Card */}
          <Card className="border-zinc-800 bg-zinc-900/50 backdrop-blur-xl shadow-2xl flex flex-col justify-between">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-white">Mode Pràctica</CardTitle>
              <CardDescription className="text-zinc-400">
                Juga sense registrar-te
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-sm text-zinc-400">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5"></div>
                  <p>Comença amb 200 fitxes</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5"></div>
                  <p>Juga contra 3 bots intel·ligents</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5"></div>
                  <p>No es guarden les teves fitxes</p>
                </div>
              </div>

              <Button
                variant="outline"
                onClick={() => navigate('/practica')}
                className="w-full border-zinc-700 hover:bg-zinc-800 text-white font-semibold h-12 text-base"
              >
                Jugar sense registrar
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center space-y-3">
          <p className="text-xs text-zinc-600">
            En crear un compte, les teves fitxes es guardaran automàticament
          </p>
          <div className="flex items-center justify-center gap-6 text-xs text-zinc-700">
            <span>Blinds: 5/10</span>
            <span>•</span>
            <span>Fitxes inicials: 1000</span>
            <span>•</span>
            <span>4 jugadors</span>
          </div>
        </div>
      </div>
    </div>
  );
}
